// Direct imports
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";

import { TraceBehavior, isPropertyBinding } from "./tracebehavior";
import type { MorphStretchData } from "../scenedata/behaviors/morphstretchdata";
import type { SceneObject } from "../core/SceneObject";
import { ValidationError } from "../scenedata/behaviordata";
import { invalidateNearestLayout } from "./layout";
import { MeshBackground } from "./meshbackground";
import { clearOpening } from "./kitOpening";
import { resolveSurfaceColorRole } from "../scenedata/theme-vocabulary";
import { Logger } from "../utils/Logger";
import { readAtBase } from "../core/canopy/PropertyResolver";
import { bakeKindOf, markBakeDirty } from "../loaders/restoreBakedSrc";
import { halvesMeetAt, MAX_MORPH_INFLUENCE } from "./morphseam";

export type MorphStretchAxis = "X" | "Y" | "Z";
type Axis = MorphStretchAxis;

/**
 * What one axis can reach on the loaded asset. See
 * {@link MorphStretch.getStretchCapability}.
 */
export interface MorphStretchAxisCapability {
    /** Measured size of the asset at rest, in meters. */
    rest: number;
    /** How a size change is realized: morph targets, or plain mesh scale. */
    via: "morph" | "scale";
    /** Whether Out morphs exist AND displace vertices. */
    canGrow: boolean;
    /** Whether In morphs exist AND displace vertices. */
    canShrink: boolean;
    /** Smallest size the morphs reach; `rest` when the axis cannot shrink. */
    min: number;
    /** Largest size the morphs reach; `rest` when the axis cannot grow. */
    max: number;
    /**
     * On a kit frame whose halves would cross below it, the smallest authored
     * size (what `width`/`height` take, meters) the geometry realizes on this
     * axis. Absent when no such floor applies. Unlike `min`, which is geometry,
     * it is in the units a control commits, so a requested size compares to it
     * directly.
     */
    floor?: number;
    /**
     * The authored inner reference at rest (meters), when the data declares
     * one — the basis of overhang-preserving sizing. `rest`/`min`/`max`
     * stay realized-outer meters; a control labeled in box units converts
     * by the constant overhang `rest − inner`. Absent when the axis sizes
     * to the box exactly.
     */
    inner?: number;
}
type AxisKey = "x" | "y" | "z";

const AXES: {
    axis: Axis;
    key: AxisKey;
    field: keyof Pick<MorphStretchData, "width" | "height" | "depth">;
    innerField: keyof Pick<MorphStretchData, "innerWidth" | "innerHeight" | "innerDepth">;
}[] = [
    { axis: "X", key: "x", field: "width", innerField: "innerWidth" },
    { axis: "Y", key: "y", field: "height", innerField: "innerHeight" },
    { axis: "Z", key: "z", field: "depth", innerField: "innerDepth" },
];

/** Morph names follow `[XYZ][pn]?_?(In|Out)` (design-set convention). */
function parseMorphName(name: string): { axis: Axis; isIn: boolean } | null {
    const m = name.match(/^([XYZ])([pn])?_?(In|Out)$/i);
    if (!m) return null;
    return { axis: m[1].toUpperCase() as Axis, isIn: m[3].toLowerCase() === "in" };
}

/** Per-mesh state captured at resolve time, before this behavior mutates anything. */
interface RestState {
    /** The mesh's own node scaling — the fallback multiplies onto this, detach restores it. */
    scaling: Vector3;
    /** mesh-local → object-root matrix, for measuring bounds in one shared space. */
    rel: Matrix;
    /** |scale| of `rel` per axis — converts mesh-local vertex deltas to root-space meters. */
    relScale: Vector3;
    /** The kit's background face (`fill_BG` / `frame_BG`), named so before any Renderer replaces its material. */
    isBackground: boolean;
}

/**
 * MorphStretch — sizes a component mesh to a target box by driving its
 * authored stretch morphs (ported from the AILayout prototype's
 * SmartStretchBehavior, the mechanism behind its "9-slice" set pieces).
 *
 * The math: per axis, the rest bounds are measured with all influences at 0
 * — in the object root's space, so multi-part GLBs with offset parts union
 * correctly; each morph group's maximum vertex displacement (×2 — both ends
 * move) is the size delta one unit of influence buys; the target size then
 * maps linearly to an influence, clamped to [0, 10] (the assets are authored
 * with ~10× overscaling headroom). A size beyond the morph range is still
 * honored — intelligent scaling, the prototype's ProxyStretch policy:
 * morphs saturate at their limit and the remainder is applied as a
 * per-axis scale on top, so detail only starts to stretch past the point
 * where the authored morphs run out. Morphless axes fall back to mesh
 * scaling entirely, applied as a multiplier on the rest scaling and
 * restored on detach. Parts are assumed
 * axis-aligned with the root (morph axes and scale axes map 1:1); the scale
 * fallback additionally assumes parts share the root origin — offset parts
 * scale about their own pivots, so multi-part sizing is only exact on morph
 * axes.
 *
 * `fit: 'contain'` replaces all of the per-axis math with one uniform,
 * aspect-preserving factor and zeroed morphs (see {@link _applyContain}) —
 * the mode for solid doodads that a box must size, never distort.
 *
 * An authored inner reference (`innerWidth`/`innerHeight`/`innerDepth`)
 * makes an axis overhang-preserving: the box sizes the inner reference and
 * the rest-outer-minus-inner difference — decorative trim — rides along
 * unchanged in absolute meters (ProxyStretch's delta sizing, made
 * data-driven; see SMART_COMPONENTS.md §4).
 *
 * Data-driven on purpose: authoring (template/wrapper materialization) writes
 * the target box in meters, and any runtime applies it at load — no layout
 * subscription. Because the stretched geometry then matches its LayoutItem
 * box, layout leaf-scaling resolves to identity and cannot distort it.
 *
 * Layout-managed nodes additionally get their resolved box handed over live
 * (`setResolvedSize`, the `LayoutSizeReceiver` duck-type) — a smart component
 * in a Fill/Fixed slot restretches when its slot does, with no authored sizes
 * required. A slot below what the asset's own morphs can reach
 * ({@link MorphStretch._shrinkFloor}) holds the shape and overhangs rather
 * than squeezing it. The resolved box is runtime state, never written back to
 * authored data.
 *
 * Export bakes the realized geometry at Base, morphs stripped and this data
 * kept (TraceExport.bakeDeformedNodes). Loaded as that bake, the component is
 * sized relative to Base (see {@link _apply}): Base is the identity, and a
 * state or page scales it.
 */
export class MorphStretch extends TraceBehavior<MorphStretchData> {
    static readonly behaviorType = "MorphStretch" as const;

    private _meshes: AbstractMesh[] = [];
    private _axisGroups = new Map<Axis, { inNames: string[]; outNames: string[] }>();
    private _baseSize: Vector3 | null = null;
    /** Centre of the rest bounds, root space — the point {@link getRestOpening} measures from. */
    private _restCentre: Vector3 | null = null;
    private _axisDeltas = new Map<Axis, { outDelta: number; inDelta: number }>();
    private _restStates = new Map<AbstractMesh, RestState>();
    /** Per axis, the smallest size at which no mesh's two halves cross — see {@link _measureSeams}. */
    private _seamMin = new Map<Axis, number>();
    /** Live layout-resolved box (runtime only — never serialized). */
    private _resolvedSize: Vector3 | null = null;
    /** Warned axis conditions ("X:degenerate"), so a bad asset warns once, not per pass. */
    private _warnedAxes = new Set<string>();
    /** Whether the meshes are an export bake, whose rest is the Base pose — see {@link _resolveMeshes}. */
    private _restIsBase = false;

    /** influence=1 ≈ this multiplier of headroom; clamp guard from the prototype. */
    private static readonly MAX_INFLUENCE = MAX_MORPH_INFLUENCE;

    /** The prototype's `minFactor` — see {@link MorphStretch._shrinkFloor}. */
    private static readonly MIN_SHRINK_FRACTION = 0.01;

    /** A moulding's opening is wider than this share of its outer size — see {@link getRestOpening}. */
    private static readonly MIN_OPENING_FRACTION = 0.5;

    constructor(sceneObject: SceneObject, data: MorphStretchData) {
        super(sceneObject, data);
        // Nothing else validates the LOAD path — SceneCreation builds
        // behaviors with `new Ctor(...)` and TraceBehavior's constructor
        // doesn't validate — so migrate here, Model's precedent. Re-resolve
        // after: the base constructor derived `_resolved` from the data as
        // it arrived, before the migration rewrote or removed anything.
        this.validate(this.getAuthored());
        this._resolved = this._resolveAll();
    }

    /**
     * Validate and migrate. Mutates `data` in place, the contract Model's
     * validate() documents, and it runs on two paths:
     *
     *  - the CONSTRUCTOR hands it `_authored` straight off the wire and
     *    ignores the return. `fit` was spelled `stretch` before #760, and
     *    TraceFrontend persisted that spelling into CMS scenes; without the
     *    rewrite such a scene loaded fine, rendered as fill, and then bricked:
     *    `_applyCommit` validates the full MERGED `_authored`, so the next
     *    commit of ANY field failed INVALID_FIT and rolled back.
     *  - a COMMIT gets the same treatment. `'stretch'` is a rename, so it is
     *    rewritten to `'fill'` with no error — `_applyCommit` keeps in-place
     *    mutations when validate returns clean, which is what lets
     *    TraceFrontend's fit toggle (still writing the old word) keep
     *    working. A genuinely unknown value is deleted AND reported: on load
     *    the delete lets the default apply; on commit `_applyCommit` restores
     *    its `prevAuthored` snapshot on any error, so the delete is thrown
     *    away with the rest of the bad patch and the user keeps their fit.
     *
     * The union stays `'fill' | 'contain'` — adding `'stretch'` to the type
     * would put it in the editor's dropdown.
     */
    protected validate(data: MorphStretchData): ValidationError[] {
        const errors: ValidationError[] = [];
        // TS can't guard wire data — a .trace file can carry anything here.
        const fit = data.fit as unknown;
        if (fit === 'stretch') {
            data.fit = 'fill';
        } else if (fit !== undefined && fit !== 'fill' && fit !== 'contain') {
            delete data.fit;
            errors.push({ field: 'fit', message: `fit must be 'fill' or 'contain'`, code: 'INVALID_FIT' });
        }
        for (const { field } of AXES) {
            const v = data[field] as unknown;
            // A `{name}`/`$name` property binding is authored data too — a
            // state-driven size ("page 2 → wide") is a declaration the
            // Properties system animates; the numeric check applies to the
            // resolved value at apply time, not the authored reference.
            if (typeof v === "string" && isPropertyBinding(v)) continue;
            if (v !== undefined && (typeof v !== "number" || !Number.isFinite(v) || v <= 0)) {
                errors.push({ field, message: `${field} must be a positive number (meters) or a property binding`, code: "INVALID_SIZE" });
            }
        }
        for (const { innerField } of AXES) {
            // Plain numbers only — the inner reference describes the asset at
            // rest, a property of the geometry, not something states animate.
            const v = data[innerField] as unknown;
            if (v !== undefined && (typeof v !== "number" || !Number.isFinite(v) || v <= 0)) {
                errors.push({ field: innerField, message: `${innerField} must be a positive number (meters)`, code: "INVALID_INNER" });
            }
        }
        const weight = data.weight as unknown;
        if (weight !== undefined && (typeof weight !== "number" || !Number.isFinite(weight) || weight <= 0)) {
            errors.push({ field: "weight", message: "weight must be a positive number", code: "INVALID_WEIGHT" });
        }
        const range = data.range as unknown;
        if (range !== undefined && !(Array.isArray(range) && range.length === 2
            && range.every(v => typeof v === "number" && Number.isFinite(v) && v > 0)
            && (range[0] as number) <= (range[1] as number))) {
            errors.push({ field: "range", message: "range must be [min, max] positive multiples of the inner reference, min ≤ max", code: "INVALID_RANGE" });
        }
        return errors;
    }

    /**
     * The authored size for one axis as a number, or undefined when nothing
     * numeric drives it — unset, an unresolved binding (no provider declares
     * the property), or a provider whose value isn't a positive number. All
     * of those mean the same thing to the geometry: the axis rests. Read
     * from the live resolution unless `from` names another (Base's).
     */
    private _sizeOf(
        field: keyof Pick<MorphStretchData, "width" | "height" | "depth">,
        from: MorphStretchData = this._resolved,
    ): number | undefined {
        const v = from[field] as unknown;
        return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
    }

    /**
     * The authored inner reference for one axis, or undefined when the axis
     * declares none — presence-gated per axis, mirroring `_sizeOf`: an axis
     * without an inner value sizes to the box exactly.
     */
    private _innerOf(
        field: keyof Pick<MorphStretchData, "innerWidth" | "innerHeight" | "innerDepth">,
        from: MorphStretchData = this._resolved,
    ): number | undefined {
        const v = from[field] as unknown;
        return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
    }

    /** The prototype's frame weight scale — 1 unless the data says otherwise. */
    private _weight(from: MorphStretchData = this._resolved): number {
        const v = from.weight as unknown;
        return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 1;
    }

    /** The prototype's proxy range as [min, max] multiples of the inner reference, or null. */
    private _range(from: MorphStretchData = this._resolved): [number, number] | null {
        const v = from.range as unknown;
        return Array.isArray(v) && v.length === 2 && v.every(n => typeof n === "number" && Number.isFinite(n) && n > 0) && v[0] <= v[1]
            ? [v[0] as number, v[1] as number]
            : null;
    }

    /**
     * Centre hook (see Layout's centre pass): where a kit asset declares an
     * inner reference, the reference is centred on the GLB's pivot by kit
     * convention and the box is met by the reference — so the pivot goes at
     * the box centre and the overhang stays where the artist put it. That is
     * the prototype's placement: its RoundedEdge base rises its 78 mm lip
     * ABOVE the box. Bounds-centring (Model's default) splits the lip across
     * both ends and drops the whole piece 39 mm. Without an inner reference
     * the hook abstains and Model's pivot compensation applies as before.
     *
     * ANY axis is enough, and that is deliberate rather than a loose gate.
     * The inner reference describes ONE box authored once for the asset, and
     * the convention is about where that box sits on the pivot — so an asset
     * that declares it at all follows the convention on all three axes. The
     * per-axis presence gate elsewhere in this behavior answers a different
     * question: whether an axis SIZES against the inner basis. An asset that
     * authors only `innerHeight` still has its pivot at the box centre; its
     * width and depth simply meet the box exactly.
     */
    getLayoutContentCenter(): { x: number; y: number; z: number } | null {
        return AXES.some(({ innerField }) => this._innerOf(innerField) !== undefined)
            ? { x: 0, y: 0, z: 0 }
            : null;
    }

    /**
     * The authored box, and the centre it declares, are in the node's own
     * frame, as if the node were not turned: a node that hugs what it holds
     * turns them.
     */
    readonly layoutContentFrame = 'node' as const;

    /**
     * Layout size provider (duck-typed — see `LayoutSizeProvider` in
     * layout.ts): a MorphStretch'd model realizes its own box by driving
     * morphs, so the layout engine must position it and never leaf-scale it —
     * scaling on top of the morphed geometry applies the size twice
     * (world = size²). The provider branch identity-restores node scale,
     * which also repairs exports that baked a leaf-scale before this
     * behavior existed on the load path.
     *
     * The provided box is the authored one even on inner-basis axes: the
     * realized outer deliberately overhangs it — decorative trim extends
     * past the slot, exactly as the prototype's visual overhung its proxy.
     */
    getLayoutContentSize(): { x: number; y: number; z: number } | null {
        // Resolved numerics, not raw authored data: a bound size counts only
        // once a provider actually supplies a number — an unresolved binding
        // is "nothing drives this axis", same as unset.
        const w = this._sizeOf("width");
        const h = this._sizeOf("height");
        const d = this._sizeOf("depth");
        if (w === undefined && h === undefined && d === undefined) return null;
        const base = this._baseSize;
        // Rest bounds unmeasured (no meshes / degenerate asset) and the box
        // only partially authored: the unsized axes have no honest answer,
        // and reporting 0 would hand layout a zero-size box. Decline to
        // provide instead — layout falls through to its own sizing.
        if (!base && (w === undefined || h === undefined || d === undefined)) {
            return null;
        }
        return {
            x: w ?? base!.x,
            y: h ?? base!.y,
            z: d ?? base!.z,
        };
    }

    /**
     * What each axis can actually reach, measured from the loaded asset —
     * for editors that want to offer a size control without inventing limits.
     *
     * An editor cannot derive this: the rest bounds come from the meshes and
     * the per-unit-of-influence delta from the morph targets' own vertex
     * displacement, both known only after attach. Without it a size field is
     * a guess, and the two ways an asset says "no" — an axis with no In
     * morphs, an axis with none at all — are invisible until the geometry
     * comes back wrong.
     *
     * `via` is how the axis realizes a change: `'morph'` (targets drive the
     * shape) or `'scale'` (no usable targets, so the mesh is scaled, which
     * stretches detail). `min`/`max` bound the morph range — soft limits:
     * a size beyond them is still realized (intelligent scaling applies the
     * remainder as plain scale), but that is where detail begins to
     * stretch, so an editor should mark, not forbid, the territory past
     * them. A `'scale'` axis reports its rest size for both, and there that
     * IS the hard floor: a layout slot narrower than a morphless axis's rest
     * stops at rest and the geometry overhangs ({@link _shrinkFloor}). On a
     * shrinkable axis `min` stays the morph range's own floor rather than the
     * apply-time one — `_apply()` never takes an axis below 1% of its rest
     * extent — so a slider built straight off `min` can point a hair past
     * what the geometry will actually realize. A kit frame is held higher
     * still, where its halves meet ({@link _measureSeams}), and reports that
     * as `floor`, in authored-size units.
     *
     * Axes whose data declares an inner reference carry it as `inner`, so a
     * control can be labeled in box units and converted (see the field doc).
     *
     * `min`/`max` are the asset's own geometric envelope — rest plus what the
     * morph targets displace — and deliberately ignore the configured
     * `weight` and `range`, which shape the realized size on top of it. A
     * slider built straight off these bounds shows what the morphs can reach,
     * not what a size in that spread will actually render as.
     *
     * Returns null before attach or when the asset has no measurable bounds.
     */
    getStretchCapability(): Record<Axis, MorphStretchAxisCapability> | null {
        const base = this._baseSize;
        if (!base) return null;
        const rests: Record<Axis, number> = { X: base.x, Y: base.y, Z: base.z };
        const out = {} as Record<Axis, MorphStretchAxisCapability>;
        for (const { axis, innerField } of AXES) {
            const rest = rests[axis];
            const group = this._axisGroups.get(axis);
            const deltas = this._axisDeltas.get(axis) ?? { outDelta: 0, inDelta: 0 };
            // Same threshold `_apply` uses to call a group degenerate: targets
            // that displace nothing are morphless in every way that matters.
            const canGrow = !!group?.outNames.length && deltas.outDelta > 1e-4;
            const canShrink = this._canShrink(axis);
            const via = canGrow || canShrink ? 'morph' : 'scale';
            out[axis] = {
                rest,
                via,
                canGrow,
                canShrink,
                min: canShrink
                    ? Math.max(0, rest - deltas.inDelta * MorphStretch.MAX_INFLUENCE)
                    : rest,
                max: canGrow
                    ? rest + deltas.outDelta * MorphStretch.MAX_INFLUENCE
                    : rest,
            };
            // Only when authored — a bare shape stays byte-identical for
            // callers that serialize or diff it.
            const inner = this._innerOf(innerField);
            if (inner !== undefined) out[axis].inner = inner;
            const floor = this._floorBox(axis, rest, inner);
            if (floor !== undefined) out[axis].floor = floor;
        }
        return out;
    }

    /**
     * Layout size receiver (duck-typed — see `LayoutSizeReceiver` in
     * layout.ts): the layout bridge hands managed nodes their resolved box
     * and this behavior realizes it with morphs instead of leaf scale.
     *
     * The resolved box is runtime state on the editor/runtime boundary:
     * authored width/height/depth are never rewritten by layout results.
     * While a resolved box is live it drives all three axes — the slot is
     * the sizing authority down to {@link _shrinkFloor}, and geometry that
     * disagrees with its box overlaps neighbours. Authored sizes still feed
     * Hug measurement (the provider above) and drive sizing whenever the
     * node is not layout-managed.
     */
    setResolvedSize(size: { x: number; y: number; z: number }): void {
        // Layout re-runs per microtask batch; skip no-op re-applies.
        if (this._resolvedSize &&
            this._resolvedSize.x === size.x &&
            this._resolvedSize.y === size.y &&
            this._resolvedSize.z === size.z) return;
        // The first box is the one the scene opened with; a later one is a
        // new size for the bake.
        if (this._resolvedSize) markBakeDirty(this.sceneObject);
        this._resolvedSize = new Vector3(size.x, size.y, size.z);
        this._apply();
    }

    /**
     * The clear opening a kit frame's moulding surrounds, in meters at rest, or
     * null before attach and for an asset that has none. It is what the
     * authored `innerWidth` / `innerHeight` of a kit frame state, so a swap to
     * another kit reads it to replace the previous kit's numbers. Measured off
     * the moulding, not the kit's background face, which does not always match
     * the opening.
     */
    getRestOpening(): { x: number; y: number } | null {
        const centre = this._restCentre;
        const base = this._baseSize;
        if (!centre || !base) return null;
        const parts = this._meshes.flatMap(mesh => {
            const rest = this._restStates.get(mesh);
            const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
            const indices = mesh.getIndices();
            return rest && !rest.isBackground && positions && indices ? [{ positions, indices, toRoot: rest.rel }] : [];
        });
        const opening = clearOpening(parts, centre);
        // A solid slab, or a face this did not recognise, leaves half the size or less, which the composer's own measure refuses as well.
        const least = MorphStretch.MIN_OPENING_FRACTION;
        return opening && base.x > 0 && base.y > 0 && opening.x > base.x * least && opening.y > base.y * least ? opening : null;
    }

    /**
     * The box a Layout last handed this behavior, or null when no Layout sizes
     * it. Runtime state, a copy: the size the piece is drawn at in its slot,
     * which an editor drag has to measure from because on a Fill axis the
     * authored data holds no size at all. The same fact `Layout.getSolvedSize` gives for a container.
     */
    getResolvedSize(): { x: number; y: number; z: number } | null {
        const box = this._resolvedSize;
        return box && { x: box.x, y: box.y, z: box.z };
    }

    /**
     * The rest-state scaling this behavior snapshotted for `mesh` at resolve
     * time, or null for meshes it does not manage (or before any apply).
     *
     * Public surface for the export bake, chosen over duck-typed access to
     * `_restStates`: the scale fallback (and a layout-resolved box on a
     * morphless axis) sizes a component on `mesh.scaling` with ZERO morph
     * influences, so export must compare live scaling against REST scaling
     * to know a node is scale-sized — and the rest snapshot is authoritative
     * only here, where it was taken before this behavior mutated anything.
     * A typed accessor keeps the export module off this class's privates
     * without freezing the internal RestState shape into the API.
     */
    getRestScaling(mesh: AbstractMesh): Vector3 | null {
        const rest = this._restStates.get(mesh);
        return rest ? rest.scaling.clone() : null;
    }

    /**
     * Show the size this component opens at — its bound sizes read at Base
     * ({@link readAtBase}) — for a bake that has to write the file's size,
     * not the state or page on screen. Returns the way back to the session's
     * view, or null when the two already agree (no binding, or Base showing):
     * then nothing is touched, so a save without states is unchanged.
     *
     * The sizes come from the same resolution and the same {@link _apply}
     * the live view uses, so the bake cannot disagree with what Base shows.
     * Only this component's meshes change; no state is switched, so nothing
     * listening for state changes hears it. The caller must bake before its
     * first await and call the returned function in a `finally`.
     */
    showBaseSizeForBake(): (() => void) | null {
        const base = readAtBase(() => this._resolveAll());
        if (JSON.stringify(base) === JSON.stringify(this._resolved)) return null;
        this._resolved = base;
        this._apply();
        return () => {
            this._resolved = this._resolveAll();
            this._apply();
        };
    }

    /**
     * The layout bridge calls this when the node leaves layout management
     * (LayoutItem removed, reparented out of the container, owning Layout
     * detached). Without it the last slot's box would win over authored
     * sizes forever — even an inspector edit of `width` would be silently
     * ignored until reload.
     */
    clearResolvedSize(): void {
        if (!this._resolvedSize) return;
        markBakeDirty(this.sceneObject);
        this._resolvedSize = null;
        this._apply();
    }

    // Meshes exist at attach time (load() resolves before attach — the same
    // contract Bevel and Renderer rely on). Asset reloads re-run attach with
    // fresh behavior instances, so no reload observer is needed.
    protected onAttach(): void {
        this._apply();
        // NOTE: deliberately no texture-projection hook here. The runtime
        // renders authored materials as-is; the anti-smear projection is an
        // editor-scoped live preview (src/editor, invoked by editor hosts
        // after load) and published scenes carry export-baked UVs.
    }

    protected async applyCommit(patch: Partial<MorphStretchData>, prev: MorphStretchData): Promise<void> {
        this._apply();
        // A size commit changes what the provider reports AND what the slot
        // should grant — but on a layout-managed node _apply prefers the
        // live resolved box, so without a re-solve nothing visible happens
        // until an unrelated edit runs a pass. Same invalidate() edge
        // text.ts and layoutitem.ts use. Covers authored edits and bound
        // values arriving through _onPropertyChanged (the patch carries the
        // resolved keys that actually changed); the prev-vs-authored compare
        // additionally catches a snapshot restore withdrawing a size key.
        const authored = this.getAuthored();
        const sizeChanged = (["width", "height", "depth"] as const).some(
            f => patch[f] !== undefined || prev[f] !== authored[f],
        );
        if (sizeChanged) invalidateNearestLayout(this.sceneObject);
    }

    protected onDetach(): void {
        // Return to the rest shape: zero stretch influences, restore scaling.
        for (const mesh of this._meshes) {
            if (mesh.isDisposed()) continue;
            const rest = this._restStates.get(mesh);
            if (rest) mesh.scaling.copyFrom(rest.scaling);
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            for (let i = 0; i < mgr.numTargets; i++) {
                const t = mgr.getTarget(i);
                if (t && parseMorphName(t.name)) t.influence = 0;
            }
        }
        this._meshes = [];
        this._axisGroups.clear();
        this._baseSize = null;
        this._restCentre = null;
        this._axisDeltas.clear();
        this._restStates.clear();
        this._seamMin.clear();
        this._resolvedSize = null;
        this._warnedAxes.clear();
        this._restIsBase = false;
    }

    private _warnOnce(key: string, message: string): void {
        if (this._warnedAxes.has(key)) return;
        this._warnedAxes.add(key);
        Logger.warn(message);
    }

    /**
     * Whether `axis` has In morphs that actually displace vertices. The one
     * inventory test behind both what {@link getStretchCapability} reports
     * and how far {@link _shrinkFloor} lets a slot shrink the geometry, so
     * the advertised capability and the realized size cannot drift apart.
     * Targets that displace nothing are morphless in every way that matters
     * — the same 1e-4 threshold `_apply` uses to call a group degenerate.
     */
    private _canShrink(axis: Axis): boolean {
        const group = this._axisGroups.get(axis);
        return !!group?.inNames.length && (this._axisDeltas.get(axis)?.inDelta ?? 0) > 1e-4;
    }

    /**
     * The smallest size `axis` may realize under a layout-resolved box, in
     * meters — the prototype's rule, ported from its `calculateMinSize`
     * (AILayout_Prototype/src/binding/SmartStretchBehavior.ts:100-121):
     * per axis, `rest × 0.01` where In morphs exist ("allow shrinking to ~1%
     * of base ... effectively unrestricted") and the FULL rest size where
     * they do not ("if no In morphs, prevent shrinking below base size").
     *
     * Derived, not authored, and deliberately not one fraction: a single
     * constant is wrong for both halves of that rule, and a MorphStretchData
     * field would let a scene claim a floor its geometry cannot hold — one
     * more number to keep in sync with the GLB it describes. The asset's own
     * morph inventory already states it, which is why a morphless axis has
     * always reported `rest` as its `min` capability; until now nothing
     * enforced that at apply time.
     *
     * This is what THIS asset's morphs can reach, read off its own morph
     * inventory — so it only binds where the asset cannot shrink at all,
     * which is exactly the case it exists for.
     */
    private _shrinkFloor(axis: Axis, rest: number): number {
        return this._canShrink(axis) ? rest * MorphStretch.MIN_SHRINK_FRACTION : rest;
    }

    /**
     * The kit minimum for `axis` ({@link _measureSeams}), or 0 when it has
     * none. A proxy range is left alone: it clamps the target to a band of the
     * inner reference and puts the remainder on plain scale, and the pieces
     * sized that way (the composer's panels, bases and backgrounds) look as they
     * do because of it. Their halves can still meet inside the band, so a ranged
     * axis can fold; flooring it would change how every one of them renders.
     * Applies to authored and layout-resolved sizes alike: no one asks for a
     * folded frame.
     */
    private _seamFloor(axis: Axis, inner: number | undefined, range: [number, number] | null): number {
        return range && inner !== undefined ? 0 : this._seamMin.get(axis) ?? 0;
    }

    /**
     * {@link _seamFloor} as the authored size that draws it, or undefined when
     * the seam is not what holds the axis: {@link _fillTarget} run backwards,
     * the inner basis's overhang taken off and the weight put back on.
     */
    private _floorBox(axis: Axis, rest: number, inner: number | undefined): number | undefined {
        const seam = this._seamFloor(axis, inner, this._range());
        if (!(seam > rest * MorphStretch.MIN_SHRINK_FRACTION)) return undefined;
        return Math.max(0, this._weight() * (seam - (inner === undefined ? 0 : rest - inner)));
    }

    /**
     * The fill arithmetic for one axis: the outer size the geometry must reach
     * (`desired`, after `floor`) for a `requested` box on an axis `rest` meters
     * long, the plain scale that rides on top of it (`extra`), and `scale`, the
     * factor the scale fallback multiplies onto rest scaling.
     */
    private static _fillTarget(
        requested: number,
        rest: number,
        inner: number | undefined,
        weight: number,
        range: [number, number] | null,
        floor: number,
    ): { outer: number; desired: number; extra: number; scale: number } {
        // The prototype's frame weight (FrameBehavior.updateSize): the
        // meshes are pre-scaled by `weight` and the geometry is fed the
        // box at 1/weight, so the box is met exactly and the overhang
        // thins with the weight — outer = box + weight × (rest − inner).
        let innerTarget = requested / weight;

        // The prototype's proxy range (ProxyStretchBehavior's intelligent
        // scaling): morphs realize an inner within [min, max] × inner and
        // the remainder is plain scale on the axis. An axis without the
        // needed morphs holds rest and takes the scale alone — which is
        // why a kit fin asked for 0.36 m renders at 0.72 m there, and a
        // 0.28 m base at 0.37 m, exactly as the prototype's kit does.
        let proxyScale = 1;
        if (inner !== undefined && range) {
            const clamped = Math.min(range[1] * inner, Math.max(range[0] * inner, innerTarget));
            proxyScale = innerTarget / clamped;
            innerTarget = clamped;
        }
        // Every scale this axis ends up with multiplies the rest scaling:
        // the morph/scale split sizes the geometry to `desired`, and
        // `extra` carries the proxy remainder and the weight.
        const extra = proxyScale * weight;

        // Inner basis (the prototype's delta sizing): the box sizes the
        // authored inner reference, and what the measured rest outer has
        // beyond it is decorative overhang, held constant in absolute
        // meters — realized outer = requested + (rest − inner). Applied
        // to the target BEFORE the morph/scale split so soft-limit
        // overflow divides against the corrected size, and before the
        // floor below so a box smaller than a negative overhang can't
        // drive the target through zero and flip the mesh.
        const outer = inner === undefined ? innerTarget : innerTarget + (rest - inner);
        const desired = Math.max(outer, floor);
        // Under a proxy range the prototype's visual never scales to cover a
        // missing morph: the axis holds rest and takes only the remainder (a
        // fin with Out morphs but no In stays 1.0 m wide and scales by
        // 0.36 / 0.5).
        const scale = (range && inner !== undefined ? 1 : desired / rest) * extra;
        return { outer, desired, extra, scale };
    }

    private _apply(): void {
        if (!this._resolveMeshes()) return;
        const base = this._baseSize;
        if (!base) return;

        if (this._resolved.fit === 'contain') {
            this._applyContain(base);
            return;
        }

        // A bake's rest is the Base pose (see _restIsBase), so a bake is sized
        // relative to Base: the scale factor for the size asked, over the
        // factor Base's own size gets on the same rest. Base is then the
        // identity, and a state or page changes the geometry by the ratio the
        // scale path gives. Sized from scratch, the inner overhang, proxy
        // remainder and weight already in the rest were applied a second
        // time: a kit base baked at 0.37 m opened at 0.21 m. The Unity app
        // sizes a bake by the same rule (Trace#2224, CanopyMorphStretchConsumer).
        const atBase = this._restIsBase ? readAtBase(() => this._resolveAll()) : null;
        const weight = this._weight();
        const range = this._range();

        for (const { axis, key, field, innerField } of AXES) {
            const inner = this._innerOf(innerField);
            // A live layout-resolved box wins over authored sizes (see
            // setResolvedSize); a non-positive resolved axis (Fill of
            // nothing) means "no meaningful size" — rest shape, like the
            // authored-undefined case. Deliberate divergence from plain
            // leaves (which collapse to MIN_VISIBLE_SCALE): near-zero morph
            // targets are outside the authored range and read as broken
            // geometry, while the rest shape reads as "content pending" —
            // the same convention Text uses for empty slots.
            //
            // Not on a bake's inner axis. Without an inner reference a bake
            // rests at the box it was baked in, so slot ÷ rest is already
            // relative to Base. With one, the rest also holds the overhang
            // and remainder realized at that box, which the file does not
            // record, so the axis follows its authored size instead, as it
            // does in a client with no layout.
            const resolved = atBase && inner !== undefined ? null : this._resolvedSize;
            const requested = resolved
                ? (resolved[key] > 0 ? resolved[key] : undefined)
                : this._sizeOf(field);
            if (requested === undefined) {
                // Authored size withdrawn (a commitSnapshot/undo restoring
                // data without this key) — return the axis to rest rather
                // than leaving the last applied stretch stranded.
                this._resetAxis(axis, key);
                continue;
            }
            const baseValue = base[key];
            if (!(baseValue > 0)) continue;

            // The shrink floor ({@link _shrinkFloor}). A live slot is the one
            // size this component never asked for, and a slot smaller than
            // what the asset's morphs can reach must not squeeze it: the axis
            // stops at its floor and the geometry OVERHANGS its box. That is
            // the prototype's layout rule 4 verbatim — "layout may request
            // smaller, but render clamps to min" (LayoutRules.ts:88-91) —
            // and it is why its fin meshes render at their native width while
            // the row keeps the authored pitch. Deliberately NOT fed back to
            // the LayoutItem: raising the box would move every sibling. So
            // the overflow is visible overhang, not silent shrinkage of the whole row.
            //
            // Authored sizes keep only the degenerate guard: an explicit
            // width for THIS instance is a statement about the instance, not
            // a slot it was dropped into, and the prototype has no authored
            // size to floor — its minSize exists to bound the layout solve.
            // The one exception is a kit frame's seam minimum: below it the
            // halves cross, which nobody asks for, so it holds for both.
            const seam = this._seamFloor(axis, inner, range);
            const authoredFloor = Math.max(baseValue * MorphStretch.MIN_SHRINK_FRACTION, seam);
            const floor = resolved ? Math.max(this._shrinkFloor(axis, baseValue), seam) : authoredFloor;
            const { outer, desired, extra, scale } = MorphStretch._fillTarget(requested, baseValue, inner, weight, range, floor);
            // What the scale branches below multiply onto rest scaling. On a
            // bake, over the factor Base's own values get on this rest (see
            // atBase), which are authored too, so they keep the authored
            // floor. An axis Base leaves unsized was baked at rest and keeps
            // the factor as it is.
            let factor = scale;
            if (atBase && !resolved) {
                const baseSize = this._sizeOf(field, atBase);
                if (baseSize !== undefined) {
                    factor /= MorphStretch._fillTarget(baseSize, baseValue, this._innerOf(innerField, atBase), this._weight(atBase),
                        this._range(atBase), authoredFloor).scale;
                }
            }
            // Under a proxy range the floor is the rule, not a surprise: the
            // remainder is already in `extra`. Only on an axis the range
            // actually reached, though — `range` is behaviour-wide but the
            // clamp in _fillTarget is gated on this axis's inner reference, so
            // an axis without one is floored the ordinary way and the author
            // still needs to hear about it.
            // The seam's floor warns only when it is what raised the floor.
            const seamRaised = seam > baseValue * MorphStretch.MIN_SHRINK_FRACTION;
            if ((resolved || seamRaised) && outer < floor && !(range && inner !== undefined)) {
                this._warnOnce(`${axis}:floor`, `[MorphStretch] "${this.sceneObject.name}" ${axis} ${resolved ? "slot" : "size"} ` +
                    `${outer.toFixed(3)} is below the asset's floor ${floor.toFixed(3)}; holding the floor ` +
                    `(geometry overhangs its box)`);
            }

            const group = this._axisGroups.get(axis);
            if (!group) {
                // No morphs on this axis — mesh scale, the prototype's
                // fallback for morphless axes. Multiplies the rest scaling
                // (authored/loader scaling is respected, and re-applying is
                // idempotent because the factor is derived from rest bounds).
                for (const mesh of this._meshes) {
                    const rest = this._restStates.get(mesh);
                    mesh.scaling[key] = (rest ? rest.scaling[key] : 1) * factor;
                }
                continue;
            }

            const deltas = this._axisDeltas.get(axis) ?? { outDelta: 0, inDelta: 0 };
            const grow = desired > baseValue;
            const delta = grow ? deltas.outDelta : deltas.inDelta;
            const calibrated = delta > 1e-4;
            const names = grow ? group.outNames : group.inNames;
            const zeroNames = grow ? group.inNames : group.outNames;

            // The requested direction has no usable morphs — either none
            // authored, or targets that displace nothing (a valid input per
            // the spec, not an error). Intelligent scaling (the prototype's
            // ProxyStretch policy): the size is still honored, realized by
            // ordinary scaling — with the honest cost that detail stretches.
            // Never feed a bare size ratio in as an influence: on assets
            // whose deltas are tiny rather than zero that explodes the mesh.
            // Warn once per axis+direction: with the receiver, _apply runs
            // per slot change, and a bad asset must not spam every pass.
            if (desired !== baseValue && !calibrated) {
                if (!names.length) {
                    this._warnOnce(`${axis}:missing`, `[MorphStretch] "${this.sceneObject.name}" ${axis} has no ` +
                        `${grow ? "Out" : "In"} morphs; reaching ${field}=${desired} by scaling (detail stretches)`);
                } else {
                    this._warnOnce(`${axis}:degenerate`, `[MorphStretch] "${this.sceneObject.name}" ${axis} ` +
                        `${grow ? "Out" : "In"} morphs displace no vertices; treating the axis as morphless (scale fallback)`);
                }
                // The scale fallback's own factor: an axis with a useless
                // morph group and one with no group at all are the same axis
                // to the asset, and must realize one size.
                for (const mesh of this._meshes) {
                    const mgr = mesh.morphTargetManager;
                    if (mgr) {
                        for (const n of [...group.inNames, ...group.outNames]) {
                            const t = mgr.getTargetByName(n);
                            if (t) t.influence = 0;
                        }
                    }
                    const rest = this._restStates.get(mesh);
                    mesh.scaling[key] = (rest ? rest.scaling[key] : 1) * factor;
                }
                continue;
            }

            // Intelligent scaling, the calibrated case: morphs realize as
            // much of the request as their range covers; any remainder is a
            // per-axis scale on top (per-axis, not uniform — the prototype's
            // own comment warns that uniform overflow overshoots the axes
            // still inside their range). Inside the range the factor is
            // exactly 1 and this is the pure morph path. The same 1%-of-rest
            // minimum bounds the reach, so the overflow division can never
            // blow up on assets whose In range passes 0.
            const reach = grow
                ? baseValue + delta * MorphStretch.MAX_INFLUENCE
                : Math.max(baseValue - delta * MorphStretch.MAX_INFLUENCE,
                    baseValue * MorphStretch.MIN_SHRINK_FRACTION);
            const morphTarget = grow ? Math.min(desired, reach) : Math.max(desired, reach);
            const raw = grow
                ? (morphTarget - baseValue) / delta
                : desired < baseValue
                    ? (baseValue - morphTarget) / delta
                    : 0;
            const influence = Math.max(0, Math.min(MorphStretch.MAX_INFLUENCE, raw));
            const overflow = desired / morphTarget;
            if (Math.abs(overflow - 1) > 1e-6) {
                this._warnOnce(`${axis}:${grow ? "over" : "under"}`, `[MorphStretch] "${this.sceneObject.name}" ${axis} ` +
                    `${field}=${desired} exceeds the morph range (${grow ? "max" : "min"} ${reach.toFixed(3)}); ` +
                    `scaling the remainder (detail stretches)`);
            }

            for (const mesh of this._meshes) {
                // Every pass fully determines BOTH scaling and influence for
                // the axis: a previous pass may have taken the scale fallback
                // or carried an overflow factor, and a stranded factor under
                // live influences would render morphed-size × factor forever.
                const rest = this._restStates.get(mesh);
                if (rest) mesh.scaling[key] = rest.scaling[key] * overflow * extra;
                const mgr = mesh.morphTargetManager;
                if (!mgr) continue;
                for (const n of zeroNames) {
                    const t = mgr.getTargetByName(n);
                    if (t) t.influence = 0;
                }
                for (const n of names) {
                    const t = mgr.getTargetByName(n);
                    if (t) t.influence = influence;
                }
            }
        }

        for (const mesh of this._meshes) {
            mesh.computeWorldMatrix(true);
            mesh.refreshBoundingInfo(false, true);
        }

        this._refreshSiblingShape();
    }

    /**
     * `fit: 'contain'` — the prototype's solidScale for doodads: solid
     * decorative pieces are often handed a per-axis box authored for another
     * component's aspect, and stretching them to it distorts what they are.
     * Instead, ONE uniform factor — the minimum of desired/rest over the
     * sized axes — scales all three mesh axes, and every stretch morph stays
     * at zero: the piece keeps its authored proportions and fits inside the
     * box, meeting it exactly only on the limiting axis. An axis with no
     * desired size doesn't constrain the min (same presence gate as the
     * stretch path), and a degenerate rest axis (flat asset) can't — a
     * factor over zero extent is meaningless. Inner-basis fields are
     * ignored here — the prototype's solidScale branch deliberately skips
     * base+delta overhang math, which thickens and off-centers a solid
     * piece instead of just sizing it.
     *
     * The shrink floor is skipped here too, and that is the prototype's
     * split, not an oversight: the rest floor belongs to the morph-stretch
     * behavior, keyed to a morph inventory this mode parks at zero. The
     * uniform-scale path is ProxyStretchBehavior, whose `getMinSize` returns
     * a flat 10-unit degeneracy guard rather than a fraction of rest
     * (AILayout_Prototype/src/binding/ProxyStretchBehavior.ts:417-428).
     * Flooring contain at rest would forbid the one thing contain exists to
     * do — shrink a solid doodad into a smaller box without distorting it.
     */
    private _applyContain(base: Vector3): void {
        const resolved = this._resolvedSize;
        let factor: number | null = null;
        for (const { key, field } of AXES) {
            const desired = resolved
                ? (resolved[key] > 0 ? resolved[key] : undefined)
                : this._sizeOf(field);
            if (desired === undefined) continue;
            const rest = base[key];
            if (!(rest > 0)) continue;
            const f = desired / rest;
            factor = factor === null ? f : Math.min(factor, f);
        }

        // No axis sized (or none measurable): rest shape, exactly like the
        // stretch path's withdrawn-size case — never a stranded factor.
        if (factor === null) {
            for (const { axis, key } of AXES) this._resetAxis(axis, key);
            return;
        }

        for (const mesh of this._meshes) {
            const rest = this._restStates.get(mesh);
            if (rest) {
                mesh.scaling.set(
                    rest.scaling.x * factor,
                    rest.scaling.y * factor,
                    rest.scaling.z * factor,
                );
            }
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            // All stretch influences to zero — a mode switch from 'fill'
            // must not leave a morphed pose under the uniform scale.
            for (let i = 0; i < mgr.numTargets; i++) {
                const t = mgr.getTarget(i);
                if (t && parseMorphName(t.name)) t.influence = 0;
            }
        }

        for (const mesh of this._meshes) {
            mesh.computeWorldMatrix(true);
            mesh.refreshBoundingInfo(false, true);
        }

        this._refreshSiblingShape();
    }


    /**
     * A sibling MeshBackground's radius clamp (and pill influence) are
     * functions of the CURRENT extents the pass that just ran changed —
     * attach order, inspector edits and per-slot setResolvedSize all reach
     * the apply paths, so their shared tail is the one chokepoint that keeps
     * its shape valid. Typed class lookup (no import cycle exists in either
     * direction) so a rename of refreshShape breaks the build here instead
     * of silently dropping the sibling re-clamp.
     */
    private _refreshSiblingShape(): void {
        this.sceneObject.getBehavior(MeshBackground)?.refreshShape();
    }

    /**
     * Return one axis to its rest state: zero both morph groups, restore the
     * rest scaling component. Used when an authored size is withdrawn, and by
     * detach (which does it for every axis).
     */
    private _resetAxis(axis: Axis, key: AxisKey): void {
        const group = this._axisGroups.get(axis);
        for (const mesh of this._meshes) {
            if (mesh.isDisposed()) continue;
            const rest = this._restStates.get(mesh);
            if (rest) mesh.scaling[key] = rest.scaling[key];
            const mgr = mesh.morphTargetManager;
            if (!mgr || !group) continue;
            for (const n of [...group.inNames, ...group.outNames]) {
                const t = mgr.getTargetByName(n);
                if (t) t.influence = 0;
            }
        }
    }

    /** Collect meshes, snapshot rest state, parse morph groups, measure, calibrate. */
    private _resolveMeshes(): boolean {
        // Every mesh must still be live: parts of a component normally dispose
        // together, but a partial disposal would leave stale refs whose rest
        // state no longer matches anything on screen.
        if (this._meshes.length && this._meshes.every(m => !m.isDisposed())) return true;

        // Meshes this object OWNS — the walk stops at every child SceneObject.
        // A child object is its own export record with its own bake plan
        // (exportFilters.ownedExportNodes draws the same line), so stretching
        // through one deformed a mesh on screen that no plan could bake: the
        // parent's candidates stop at the child, and the child, having no
        // MorphStretch of its own, plans nothing. It shipped at rest while the
        // screen showed it stretched. One boundary for deformation and export
        // — a child object that should stretch gets its own MorphStretch.
        const meshes = this.sceneObject.getOwnedMeshes().filter(m => m instanceof Mesh);
        if (!meshes.length) {
            Logger.warn(`[MorphStretch] "${this.sceneObject.name}" has no meshes to stretch`);
            return false;
        }
        this._meshes = meshes;

        // A deformation bake is the Base pose: the export poses every bound
        // size at Base before it bakes (showBaseSizeForBake), so the rest
        // measured below already holds what Base's sizes realized. It is known
        // by the name the export gave its file, which every bake carries. The
        // Provenance note is not the signal: it is written only when the rig
        // travels, and older files have none. A Bevel bake is not one: it is a
        // rounded primitive, and a writer that bakes no deformation leaves the
        // size to this behavior. An authoring load that has the note opens
        // the rig instead, so this holds only where the bake itself was loaded.
        this._restIsBase = bakeKindOf(this.sceneObject.src) === "deformation";

        // Rest snapshot first — everything below (and _apply) is expressed
        // relative to it. Meshes are fresh here: either first attach or a
        // reload replaced them, so nothing of ours is baked in yet.
        const root = this.sceneObject.transform;
        root.computeWorldMatrix(true);
        const invRoot = root.getWorldMatrix().clone().invert();
        this._restStates.clear();
        for (const mesh of meshes) {
            mesh.computeWorldMatrix(true);
            const rel = mesh.getWorldMatrix().multiply(invRoot);
            const relScale = new Vector3();
            rel.decompose(relScale);
            relScale.set(Math.abs(relScale.x), Math.abs(relScale.y), Math.abs(relScale.z));
            this._restStates.set(mesh, { scaling: mesh.scaling.clone(), rel, relScale, isBackground: resolveSurfaceColorRole(mesh.material?.name) !== null });
        }

        // Morph groups from the first managed mesh (parts share the rig).
        this._axisGroups.clear();
        for (const mesh of meshes) {
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            for (let i = 0; i < mgr.numTargets; i++) {
                const t = mgr.getTarget(i);
                if (!t) continue;
                const parsed = parseMorphName(t.name);
                if (!parsed) continue;
                let group = this._axisGroups.get(parsed.axis);
                if (!group) { group = { inNames: [], outNames: [] }; this._axisGroups.set(parsed.axis, group); }
                const list = parsed.isIn ? group.inNames : group.outNames;
                if (!list.includes(t.name)) list.push(t.name);
            }
            break; // rig lives on the first managed mesh; all parts share names
        }

        this._restCentre = this._measureRestBounds();
        this._calibrate();
        this._measureSeams();
        return true;
    }

    /**
     * Union bounds in the object root's space with every stretch influence
     * at 0. Each mesh's local box corners go through its mesh→root matrix,
     * so offset parts of multi-part GLBs land where they render instead of
     * stacking their pivot-centered local boxes. Returns the centre of those
     * bounds, which is where a kit frame's opening is measured from.
     */
    private _measureRestBounds(): Vector3 | null {
        const snapshot: [AbstractMesh, Map<string, number>][] = [];
        for (const mesh of this._meshes) {
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            const m = new Map<string, number>();
            for (let i = 0; i < mgr.numTargets; i++) {
                const t = mgr.getTarget(i);
                if (t) { m.set(t.name, t.influence); t.influence = 0; }
            }
            snapshot.push([mesh, m]);
        }

        const min = new Vector3(Infinity, Infinity, Infinity);
        const max = new Vector3(-Infinity, -Infinity, -Infinity);
        const corner = new Vector3();
        for (const mesh of this._meshes) {
            mesh.refreshBoundingInfo(false, true);
            const rel = this._restStates.get(mesh)?.rel;
            if (!rel) continue;
            const bb = mesh.getBoundingInfo().boundingBox;
            for (const v of bb.vectors) {
                Vector3.TransformCoordinatesToRef(v, rel, corner);
                min.minimizeInPlace(corner);
                max.maximizeInPlace(corner);
            }
        }

        for (const [mesh, m] of snapshot) {
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            for (const [name, value] of m) {
                const t = mgr.getTargetByName(name);
                if (t) t.influence = value;
            }
        }

        this._baseSize = Number.isFinite(min.x) ? max.subtract(min) : null;
        return this._baseSize && min.add(this._baseSize.scale(0.5));
    }

    /**
     * Per axis: the largest vertex displacement any of the group's targets
     * produces, ×2 (both ends move) — the size change one influence unit
     * buys. Displacements are mesh-local, so scale by the mesh's rest
     * mesh→root scale to stay in the same meters as the measured bounds.
     */
    private _calibrate(): void {
        this._axisDeltas.clear();
        for (const [offset, { axis, key }] of AXES.entries()) {
            const group = this._axisGroups.get(axis);
            if (!group) continue;
            const maxShift = (names: string[]): number => {
                let out = 0;
                for (const mesh of this._meshes) {
                    const mgr = mesh.morphTargetManager;
                    if (!mgr) continue;
                    const basePos = mesh.getVerticesData(VertexBuffer.PositionKind);
                    if (!basePos) continue;
                    const toRoot = this._restStates.get(mesh)?.relScale[key] ?? 1;
                    for (const name of names) {
                        const targetPos = mgr.getTargetByName(name)?.getPositions();
                        if (!targetPos) continue;
                        for (let i = offset; i < targetPos.length; i += 3) {
                            const d = Math.abs(targetPos[i] - basePos[i]) * toRoot;
                            if (d > out) out = d;
                        }
                    }
                }
                return out * 2;
            };
            this._axisDeltas.set(axis, { outDelta: maxShift(group.outNames), inDelta: maxShift(group.inNames) });
        }
    }

    /**
     * Per planar axis, the smallest size at which no mesh's two halves cross.
     *
     * A kit frame shrinks by sliding its n and p halves together over a
     * stretching seam. The morphs reach 1% of rest, but the halves meet long
     * before that: past the meeting point the corners overlap. The point is read
     * off the vertex data, as the calibration is: the largest In influence at
     * which the halves stay apart, then the size that influence buys.
     *
     * X and Y only. Z is a kit's thickness, and the kits are used thinner than
     * their Z halves allow (a 30 mm frame on a kit whose halves meet at 71 mm).
     * An axis that cannot shrink, or whose halves never meet within the morph
     * range, has no entry.
     */
    private _measureSeams(): void {
        this._seamMin.clear();
        const base = this._baseSize;
        if (!base) return;
        for (const [offset, { axis, key }] of AXES.slice(0, 2).entries()) {
            if (!this._canShrink(axis)) continue;
            const names = this._axisGroups.get(axis)!.inNames;
            let meet = Infinity;
            for (const mesh of this._meshes) meet = Math.min(meet, this._halvesMeet(mesh, offset, names));
            if (meet < MorphStretch.MAX_INFLUENCE) {
                this._seamMin.set(axis, base[key] - meet * this._axisDeltas.get(axis)!.inDelta);
            }
        }
    }

    /** The In influence at which `mesh`'s halves meet along root axis `a`, in root-space meters. */
    private _halvesMeet(mesh: AbstractMesh, a: number, inNames: string[]): number {
        const mgr = mesh.morphTargetManager;
        const basePos = mesh.getVerticesData(VertexBuffer.PositionKind);
        const rel = this._restStates.get(mesh)?.rel;
        if (!mgr || !basePos || !rel) return Infinity;
        const m = rel.m;
        const count = basePos.length / 3;
        const rest = new Float64Array(count);
        const shift = new Float64Array(count);
        for (let i = 0; i < count; i++) {
            rest[i] = basePos[i * 3] * m[a] + basePos[i * 3 + 1] * m[4 + a] + basePos[i * 3 + 2] * m[8 + a] + m[12 + a];
        }
        for (const name of inNames) {
            const target = mgr.getTargetByName(name)?.getPositions();
            if (!target) continue;
            for (let i = 0; i < count; i++) {
                const j = i * 3;
                shift[i] += (target[j] - basePos[j]) * m[a] + (target[j + 1] - basePos[j + 1]) * m[4 + a]
                    + (target[j + 2] - basePos[j + 2]) * m[8 + a];
            }
        }
        return halvesMeetAt(rest, shift, MorphStretch.MAX_INFLUENCE);
    }
}
